import React from 'react'
import type { NextPage, GetServerSidePropsContext } from 'next'
import { useTranslations } from 'next-intl'
import { Box, useColorModeValue, Text, Link } from '@chakra-ui/react'
import { InfoFrame } from '@/components/InfoFrame'
import { PageHero } from '@/components/PageHero'
import { Highlights } from '@/components/Highlights'
import { ContainerBase } from '@/components/containers/ContainerBase'
import { SectionWithCards } from '@/components/SectionWithCards'
import { ShopNowCard } from '@/components/ShopNowCard'
import { UpgradeIcon } from '@/components/icons/UpgradeIcon'
import { ApprovalIcon } from '@/components/icons/ApprovalIcon'
import { CheckOutlineIcon } from '@/components/icons/CheckOutlineIcon'
import { SUPPORT_RETIREMENT_UPGRADE_URL } from '@/constants'
import { HighlightItem } from '@/types/highlights.interface'
import SeoHead from '@/components/seo-head'
import type { CardData } from '@/components/SectionWithCards'

const MinersUpgradePage: NextPage = () => {
  const t = useTranslations()
  const cardBg = useColorModeValue('white', 'black')
  const cardTitleColor = useColorModeValue('black', 'white')
  const cardDescriptionColor = useColorModeValue(
    'blackAlpha.800',
    'whiteAlpha.800'
  )
  const cardBorderColor = useColorModeValue('blackAlpha.500', 'whiteAlpha.500')
  const bg = useColorModeValue('blackAlpha.50', 'whiteAlpha.100')

  const items: HighlightItem[] = [
    {
      icon: UpgradeIcon,
      title: t('miners-upgrade.upgrade-notification'),
      description: t('miners-upgrade.upgrade-opportunity'),
    },
    {
      icon: ApprovalIcon,
      title: t('miners-upgrade.review-accept'),
      description: t('miners-upgrade.upgrade-accepted'),
    },
    {
      icon: CheckOutlineIcon,
      title: t('miners-upgrade.instant-activation'),
      description: t('miners-upgrade.takes-effect-immediately'),
    },
  ]

  const smartUpgrades: CardData[] = [
    {
      title: t('miners-upgrade.maximize-fleet-efficiency'),
      description: t('marketplace-landing.buy-sell-asics'),
      iconImage: { url: '/img/fleet.png' },
    },
    {
      title: t('miners-upgrade.increased-rewards'),
      description: t('miners-upgrade.more-efficient-miners'),
      iconImage: { url: '/img/maximize-returns.png' },
      iconImageMobile: { url: '/img/maximize-returns-mobile.png' },
    },
    {
      title: t('miners-upgrade.hassle-free'),
      description: t('miners-upgrade.avoid-complexities'),
      iconImage: { url: '/img/hassle-free.jpg' },
      iconImageMobile: { url: '/img/hassle-free-mobile.jpg' },
    },
    {
      title: t('miners-upgrade.no-wait-time'),
      description: t('miners-upgrade.discount-coupon'),
      iconImage: { url: '/img/phone-use.png' },
    },
  ]

  return (
    <>
      <SeoHead
        title={t('miners-upgrade.seo.title')}
        breadcrumbTitle={t('miners-upgrade.seo.breadcrumb')}
        description={t('miners-upgrade.seo.description')}
      />
      <PageHero
        title={t('miners-upgrade.maximize-efficiency')}
        subTitle={t('miners-upgrade.miner-upgrade')}
        description={t('miners-upgrade.stay-competitive')}
        image="img/miner-upgrade-hero.png"
        mobileImage="img/miner-upgrade-hero.png"
        anchor="what-is-miner-upgrade"
      />
      <Box id="what-is-miner-upgrade">
        <InfoFrame
          title={t('miners-upgrade.what-is-miner-upgrade')}
          description={t.rich('miners-upgrade.simplified-transitioning', {
            br: () => <br />,
          })}
        />
      </Box>
      <Highlights
        title={t('miners-upgrade.optimize-operations')}
        items={items}
        columns={3}
      />
      <ShopNowCard />
      <Box bg={bg}>
        <ContainerBase
          px={{ base: '32px', md: 4 }}
          py={{ base: '42px', md: '100px' }}
        >
          <SectionWithCards
            title={t('miners-upgrade.maximize-mining')}
            subtitle={t('miners-upgrade.financially-healthy')}
            cards={smartUpgrades}
            cardImageOverlay={false}
            cardImageBg="gray.890"
            cardContentPy={8}
            cardBg={cardBg}
            cardTitleColor={cardTitleColor}
            cardDescriptionColor={cardDescriptionColor}
            cardBorderColor={cardBorderColor}
            cardImageHeight="252px"
            alignStart
            columns={2}
          />
          <Text
            variant="spaceGrotesk"
            fontSize={{ base: '16px', md: '24px' }}
            fontWeight="300"
          >
            {t.rich('miners-upgrade.more-details', {
              link: (children) => (
                <Link
                  variant="black"
                  textDecoration="underline"
                  href={SUPPORT_RETIREMENT_UPGRADE_URL}
                >
                  {children}
                </Link>
              ),
              link2: (children) => (
                <Link
                  variant="black"
                  textDecoration="underline"
                  href="/hosting-agreement"
                >
                  {children}
                </Link>
              ),
            })}
          </Text>
        </ContainerBase>
      </Box>
    </>
  )
}

export async function getStaticProps({ locale }: GetServerSidePropsContext) {
  return {
    props: {
      messages: (await import(`../../../locales/${locale}.json`)).default,
    },
  }
}

export default MinersUpgradePage
